import { useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import ProductCardSlant from "./ProductCard";
import bgPattern from "../assets/prodbag.jpeg";
import defaultImage from "../assets/default.png";
import { products as staticProducts } from "../data/products";

// Import your product images
import encrePrimaire from "../assets/encrePrimaire.png";
import encrePantone from "../assets/pantone.png";
import encreUV from "../assets/encreUV.png";
import plaques from "../assets/plaquesMachine.png";
import dorure from "../assets/dorure.png";
import pelliculage from "../assets/pelliculage.png";
import fildelin from "../assets/fildelin.png";
import reliureSpirale from "../assets/reliureSpirale.png";
import blanchet from "../assets/blanchet.png";
import maculante from "../assets/antimaculante.jpg";
import fildefer from "../assets/fildefer.png";
import reglette from "../assets/reglette.png";
import etirable from "../assets/etirable.png";
import aiguille from "../assets/aiguille.png";
import wash from "../assets/wash60.png";
import mouillage from "../assets/mouillage.png";
import ipa from "../assets/ipa.png";
import vernis from "../assets/vernis.png";
import gum from "../assets/gum.png";
import plateCleaner from "../assets/plateCleaner.png";

// Map filenames to imports
const imageMap = {
  "encrePrimaire.png": encrePrimaire,
  "encrePantone.png": encrePantone,
  "encreUV.png": encreUV,
  "plaques.png": plaques,
  "dorure.png": dorure,
  "pelliculage.png": pelliculage,
  "fildelin.png": fildelin,
  "reliureSpirale.png": reliureSpirale,
  "blanchet.png": blanchet,
  "maculante.png": maculante,
  "fildefer.png": fildefer,
  "reglette.png": reglette,
  "etirable.png": etirable,
  "aiguille.png": aiguille,
  "wash.png": wash,
  "mouillage.png": mouillage,
  "ipa.png": ipa,
  "vernis.png": vernis,
  "gum.png": gum,
  "plateCleaner.png": plateCleaner,
};

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const clashDisplay = { fontFamily: "'Clash Display', sans-serif" };

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return staticProducts.filter(product =>
      product.title?.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query) ||
      product.longDescription?.toLowerCase().includes(query) ||
      product.category?.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group filtered products by category
  const groupedProducts = useMemo(() => {
    const categories = [...new Set(filteredProducts.map((p) => p.category))];
    return categories.map((cat) => ({
      category: cat,
      items: filteredProducts.filter((p) => p.category === cat),
    }));
  }, [filteredProducts]);

  return (
    <div className="w-full bg-black relative">
      <Navbar />

      {/* Background Pattern */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, black 0%, transparent 10%, transparent 50%, black 80%), url(${bgPattern})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "120%",
            backgroundPosition: "top center",
          }}
        />
      </div>

      {/* Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `
            linear-gradient(
              to bottom,
              rgba(0,0,0,0.5) 0%,
              rgba(0,0,0,0.6) 20%,
              rgba(0,0,0,0.7) 50%,
              rgba(0,0,0,0.6) 75%,
              rgba(0,0,0,0.5) 90%
            )
          `,
        }}
      />

      {/* Main Content */}
      <main className="relative z-10 py-12 flex flex-col gap-16">
        <div className="relative z-10 mt-10 md:mt-24 max-w-8xl px-4 py-24 text-center mx-auto">
          <p className="text-xl sm:text-2xl md:text-h3 font-bold text-white mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
            Résultats de recherche
          </p>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Clash Display', sans-serif" }}>
            "{searchQuery}"
          </h1>

          <p className="text-base sm:text-lg text-white/80 mb-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} trouvé{filteredProducts.length !== 1 ? 's' : ''}
          </p>

          <Link
            to="/produits"
            className=" text-white border-[3px] border-white text-lg sm:text-xl font-bold bg-transparent hover:bg-white hover:text-black px-8 sm:px-6 py-3 sm:py-4 mt-14 sm:mt-14 rounded-[10px]"
    style={clashDisplay}>
            Voir tous les produits
          </Link>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="w-full flex justify-center px-4">
            <div className="bg-white rounded-[60px] shadow-lg max-w-4xl w-full px-6 lg:px-16 py-16 text-center">
              <div className="text-6xl mb-6">🔍</div>
              <h2 className="text-2xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Aucun résultat trouvé
              </h2>
              <p className="text-gray-600 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Nous n'avons pas trouvé de produit correspondant à "<strong>{searchQuery}</strong>"
              </p>
              <p className="text-gray-500 text-sm mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Essayez avec d'autres mots-clés ou parcourez nos catégories
              </p>
              <Link
                to="/produits"
                className="inline-block px-8 py-3 bg-[#158DC1] text-white rounded-[15px] hover:bg-[#0d6b8f] transition"
              >
                Parcourir tous les produits
              </Link>
            </div>
          </div>
        ) : (
          groupedProducts.map((group) => (
            <section key={group.category} className="w-full flex justify-center px-4">
              <div className="bg-white rounded-[60px] shadow-lg max-w-8xl w-full px-6 lg:px-16 py-12">
                <h2 className="text-2xl sm:text-3xl text-left md:text-4xl font-bold mb-12 text-black" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                  {group.category}
                  <span className="text-gray-500 text-xl ml-3">
                    ({group.items.length})
                  </span>
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                  {group.items.map((product) => (
                    <div key={product.id} className="flex flex-col items-center relative">
                      <ProductCardSlant
                        id={product.id}
                        {...product}
                        image={imageMap[product.image] || defaultImage}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ))
        )}
      </main>

      <Footer />
    </div>
  );
}